import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MapPin, Clock, ShieldCheck } from "lucide-react";
import { clinicConfig } from "@/config/clinic";
import { useOpenStatus } from "@/hooks/useOpenStatus";

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HERO_BG =
  "https://images.unsplash.com/photo-1633642571632-a6fb3160e3b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxhcHBhbGFjaGlhbiUyMG1vdW50YWlucyUyMGZvcmVzdCUyMG1pc3R8ZW58MHx8fHwxNzgzOTc1MjMwfDA&ixlib=rb-4.1.0&q=85";

export const Hero = ({ onRequest }) => {
  const ref = useRef(null);
  const status = useOpenStatus();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden bg-primary"
    >
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 z-0"
      >
        <img
          src={HERO_BG}
          alt="Appalachian mountains near Keystone, West Virginia"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/80 mix-blend-multiply" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/40 to-primary/70" />
      </motion.div>

      <div className="relative z-10 mx-auto flex min-h-[92vh] max-w-7xl flex-col justify-end px-6 pb-16 pt-32 md:px-12 md:pb-24 md:pt-40">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="mb-8 flex items-center gap-3"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/25 bg-primary-foreground/10 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-primary-foreground/90 backdrop-blur-sm">
            <span
              className={`h-2 w-2 rounded-full ${
                status.isOpen ? "bg-emerald-400" : "bg-amber-400"
              }`}
            />
            {status.label} · Keystone, WV
          </span>
        </motion.div>

        <h1
          data-testid="hero-heading"
          className="font-serif text-5xl font-medium leading-[0.95] tracking-tight text-primary-foreground md:text-7xl lg:text-8xl"
        >
          <span className="block overflow-hidden">
            <motion.span custom={0} variants={line} initial="hidden" animate="visible" className="block">
              Primary care in
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span custom={1} variants={line} initial="hidden" animate="visible" className="block italic text-accent">
              Keystone, West Virginia.
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55 }}
          className="mt-7 max-w-xl text-lg leading-relaxed text-primary-foreground/85 md:text-xl"
        >
          See an experienced family medicine physician close to home — with
          virtual care options through Doc Talk.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
        >
          <a
            href={clinicConfig.patientPortalUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-signin-cta"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-accent px-7 py-4 text-base font-semibold text-accent-foreground transition-transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-primary-foreground"
          >
            Patient Sign In
            <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.75} />
          </a>
          <button
            onClick={onRequest}
            data-testid="hero-book-cta"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-primary-foreground/30 bg-primary-foreground/5 px-7 py-4 text-base font-semibold text-primary-foreground backdrop-blur-sm transition-colors hover:bg-primary-foreground/15 focus:outline-none focus:ring-2 focus:ring-primary-foreground"
          >
            Book or Request Care
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-primary-foreground/15 pt-6 text-sm text-primary-foreground/80"
        >
          <span className="inline-flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-accent" strokeWidth={1.5} />
            {clinicConfig.experience}
          </span>
          <span className="inline-flex items-center gap-2">
            <MapPin className="h-4 w-4 text-accent" strokeWidth={1.5} />
            {clinicConfig.addressLine1}
          </span>
          <span className="inline-flex items-center gap-2">
            <Clock className="h-4 w-4 text-accent" strokeWidth={1.5} />
            Open Mon–Thu, 9 a.m.–5 p.m.
          </span>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
